import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';
import { FormikProvider, useFormik } from 'formik';
import { useLazyQuery } from '@apollo/client';
import { Trans, useTranslation } from 'react-i18next';

import {
  confirmPasswordValidator,
  passwordValidator,
} from '../../components/form/Input/validators/password-validator';

import { CHECK_REFERRAL_CODE } from '../../http/apollo-queries';

import { Button } from '../../components/UI';
import { Input, Checkbox } from '../../components/form';

import { useAuth } from '../../context/Auth';
import { useSignUpContext } from './SignUpContext';
import { ReferralCodeValidator } from '../../components/form/Input/validators/ReferralCodeValidator';
import { logger } from '../../helpers/logger';

const SignUpFinalForm = () => {
  const { t } = useTranslation();
  const { signUp } = useAuth();
  const { state, dispatch } = useSignUpContext();
  const navigate = useNavigate();

  const [signUpError, setSignUpError] = useState(null as any);
  const [referralCodeDiscount, setReferralCodeDiscount] = useState(-1);
  const [checkReferralCode, { loading: referralCodeLoading }] =
    useLazyQuery(CHECK_REFERRAL_CODE);

  const formik = useFormik({
    initialValues: {
      email: state.email,
      password: '',
      confirmPassword: '',
      referralCode: '',
      signupTerms: false,
    },
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validate: (values) => {
      const errors: any = {};
      if (!values.signupTerms) {
        errors.signupTerms = 'Required';
      }
      return errors;
    },
    onSubmit: (values) => {
      dispatch({
        type: 'SET_PASSWORD',
        payload: values.password,
      });
      dispatch({
        type: 'SET_REFERRAL_CODE',
        payload: values.referralCode,
      });

      signUp(state.email, values.password, values.referralCode)
        .then((res) => {
          logger.log('Signup:Response', res);

          if (res.userConfirmed) {
            navigate(`/choose-username`);
          } else {
            dispatch({
              type: 'SET_STEP',
              payload: 'CONFIRM_EMAIL',
            });
          }
        })
        .catch((e) => {
          logger.error('signupError', e.code);

          setSignUpError(
            t(`signUp.form.error.${e.code}`, { defaultValue: e.message })
          );

          formik.setSubmitting(false);
        });
    },
  });

  useEffect(() => {
    logger.log(
      'Signup:referralCode',
      formik.values.referralCode,
      formik.errors.referralCode,
      formik.touched.referralCode
    );

    if (formik.values.referralCode && !formik.errors.referralCode) {
      logger.log('check');

      checkReferralCode({
        variables: {
          referralCode: formik.values.referralCode,
        },
      }).then(({ data }) => {
        setReferralCodeDiscount(
          data?.checkReferralCode?.status === 'SUCCESS'
            ? data?.checkReferralCode?.discountPercentage
            : 0
        );
      });
    } else {
      setReferralCodeDiscount(-1);
    }

    if (formik.touched.referralCode && !formik.values.referralCode) {
      logger.log('reset');
      formik.setFieldTouched('referralCode', false);
    }
  }, [formik.errors.referralCode, formik.values.referralCode]);

  return (
    <>
      <h1
        className={
          'text-black text-xl font-semibold text-center dark:text-white'
        }
      >
        {t('signUp.form.title')}
      </h1>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit} className="w-full">
          <div className="mt-8">
            <Input
              name="email"
              id="email"
              readOnly={true}
              label={t('signUp.form.label.email')}
              size="full"
              type="text"
              showLabel={true}
              value={state.email}
            />
          </div>
          <div className="mt-8">
            <Input
              name="referralCode"
              id="referralCode"
              formik={formik}
              label={t('signUp.form.label.referralCode')}
              size="full"
              type="text"
              showLabel={true}
              validate={ReferralCodeValidator}
              placeholder={t('signUp.form.placeholder.referralCode')}
              errorMessage={
                formik.errors.referralCode && formik.touched.referralCode
                  ? t(
                      'signUp.form.error.referralCode.' +
                        formik.errors.referralCode
                    )
                  : ''
              }
            />
            {referralCodeDiscount > -1 && (
              <small className="text-positive text-xs">
                {t('signUp.form.referralCodeDiscount', {
                  discount: referralCodeDiscount,
                })}
              </small>
            )}
          </div>

          <div className="mt-8">
            <Input
              name="password"
              formik={formik}
              id="password"
              showPasswordHint={true}
              validate={passwordValidator}
              label={t('signUp.form.label.password')}
              size="full"
              type="password"
              placeholder={t('signUp.form.placeholder.password')}
            />
          </div>
          <div className="mt-8">
            <Input
              name="confirmPassword"
              formik={formik}
              validate={(value: string) =>
                confirmPasswordValidator(value, formik.values.password)
              }
              id="confirmPassword"
              label={t('signUp.form.label.confirmPassword')}
              size="full"
              type="password"
              placeholder={t('signUp.form.placeholder.confirmPassword')}
              errorMessage={
                formik.errors.confirmPassword && formik.touched.confirmPassword
                  ? t('signUp.form.error.confirmPassword')
                  : ''
              }
            />
          </div>
          <div className="mt-4 w-full">
            <Checkbox
              id="signupTerms"
              name="signupTerms"
              formik={formik}
              shape={'square'}
            >
              <label
                htmlFor="signupTerms"
                className={'w-64 text-xs dark:text-white'}
              >
                <Trans i18nKey="signUp.form.label.signupTerms">
                  accept
                  <Link
                    className={'underline'}
                    to={'/terms-and-conditions'}
                    target={'_blank'}
                  >
                    term
                  </Link>
                  <Link
                    className={'underline'}
                    to={'/privacy-policy'}
                    target={'_blank'}
                  >
                    privacy
                  </Link>
                </Trans>
              </label>
            </Checkbox>
            <div className={'text-negative text-xs'}>
              {formik.errors.signupTerms && formik.touched.signupTerms
                ? t('signUp.form.error.signupTerms')
                : ''}
            </div>
          </div>
          <Button
            type="submit"
            variant="primary"
            buttonClass="w-full mt-8"
            loading={formik.isSubmitting}
          >
            {t('signUp.form.label.submit')}
          </Button>

          <div className="mt-8">
            <div className="mt-4 text-center">
              <div className="text-sm text-grey-50 cursor-pointer">
                <Trans i18nKey="signUp.form.haveAccount">
                  have_account
                  <Link
                    className={'text-black font-semibold dark:text-white'}
                    to={'/signin'}
                  >
                    login
                  </Link>
                </Trans>
              </div>
            </div>
          </div>
        </form>
      </FormikProvider>
    </>
  );
};
export default SignUpFinalForm;
